import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  ZoomIn, 
  Filter, 
  ArrowLeft, 
  Loader2, 
  Image as ImageIcon,
  Phone,
  Mail
} from 'lucide-react';
import { contactInfo } from '../data';
import ScrollReveal from '../components/ScrollReveal';
import useDocumentMetadata from '../hooks/useDocumentMetadata';

export default function Gallery() {
  const navigate = useNavigate();
  
  // Set SEO metadata
  useDocumentMetadata(
    'Se vores haveprojekter & referencer | BP Anlægsgartner',
    'Se billeder af vores udførte haveprojekter, belægningsarbejde og havepleje i Thy & Vestjylland. Lad dig inspirere og få et uforpligtende tilbud!'
  );

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('Alle');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Fetch gallery items from CMS
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://api.dev.auxo.dk/public/content/gallery');
        if (!response.ok) {
          throw new Error('Kunne ikke hente galleribilleder');
        }
        const data = await response.json();
        setItems(data);
      } catch (err) {
        console.error('Fejl ved hentning af galleri:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, []);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Extract unique categories
  const categories = ['Alle', ...new Set(items.map(item => item.category?.da || item.category).filter(Boolean))];

  // Filtered items
  const filteredItems = selectedCategory === 'Alle' 
    ? items 
    : items.filter(item => (item.category?.da || item.category) === selectedCategory);

  // Lightbox navigation
  const handlePrev = (e) => {
    e?.stopPropagation();
    if (lightboxIndex === null) return;
    setLightboxIndex((prev) => (prev === 0 ? filteredItems.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e?.stopPropagation();
    if (lightboxIndex === null) return;
    setLightboxIndex((prev) => (prev === filteredItems.length - 1 ? 0 : prev + 1));
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape') setLightboxIndex(null);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, filteredItems]);

  return (
    <div className="min-h-screen bg-stone-50 text-stone-800 pb-16 selection:bg-emerald-600 selection:text-white">
      
      {/* Hero Header */}
      <div className="relative bg-emerald-950 pt-44 pb-16 sm:pt-48 md:pt-56 md:pb-24 lg:pt-64 overflow-hidden border-b border-emerald-900/40">
        {/* Decorative background patterns */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-emerald-500 blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-emerald-600 blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              {/* Breadcrumbs */}
              <div className="flex items-center gap-2 text-xs font-semibold text-white/60 uppercase tracking-wider mb-3">
                <Link to="/" className="hover:text-white transition-colors">Forside</Link>
                <span>/</span>
                <span className="text-white">Galleri</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight">
                Vores Arbejde
              </h1>
              <p className="mt-3 text-base md:text-lg text-white/90 max-w-2xl">
                Gå på opdagelse i vores udførte projekter i Midt- og Vestjylland. Vi sætter en ære i det gode håndværk og de holdbare løsninger.
              </p>
            </div>
            
            <div>
              <Link
                to="/"
                onClick={() => {
                  setTimeout(() => {
                    const el = document.getElementById('kontakt');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="inline-flex items-center gap-2 bg-white text-emerald-950 hover:bg-emerald-50 font-bold px-4 sm:px-6 py-2.5 sm:py-3 rounded-full shadow-lg transition-all hover:-translate-y-0.5 text-xs sm:text-sm md:text-base"
              >
                Få et uforpligtende tilbud
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Category Filter */}
        {!loading && !error && items.length > 0 && (
          <ScrollReveal variant="fade-up" duration={800}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10 pb-6 border-b border-stone-200">
              <div className="flex items-center gap-2 text-stone-500 text-sm font-medium">
                <Filter className="h-4 w-4 text-emerald-600" />
                <span>Filtrer efter kategori:</span>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all cursor-pointer ${
                      selectedCategory === category
                        ? 'bg-emerald-600 text-white shadow-md shadow-emerald-900/10'
                        : 'bg-stone-200 text-stone-700 hover:bg-stone-300 hover:text-stone-900'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </ScrollReveal>
        )}

        {/* Loading State */}
        {loading && (
          <div className="py-24 flex flex-col items-center justify-center">
            <Loader2 className="h-12 w-12 text-emerald-600 animate-spin mb-4" />
            <p className="text-stone-500 font-medium">Henter galleribilleder...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="py-16 text-center max-w-md mx-auto bg-white border border-stone-200 rounded-2xl p-8 shadow-sm">
            <div className="h-12 w-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4">
              <X className="h-6 w-6" />
            </div>
            <h3 className="text-lg font-bold text-stone-900 mb-2">Der opstod en fejl</h3>
            <p className="text-stone-500 text-sm mb-6">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold rounded-xl transition-colors"
            >
              Prøv igen
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && items.length === 0 && (
          <div className="py-24 text-center max-w-md mx-auto">
            <div className="h-16 w-16 rounded-full bg-stone-200 text-stone-500 flex items-center justify-center mx-auto mb-4">
              <ImageIcon className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold text-stone-900 mb-2">Ingen billeder fundet</h3>
            <p className="text-stone-500 text-sm">Der er i øjeblikket ingen billeder i galleriet. Tjek venligst igen senere.</p>
          </div>
        )}

        {/* Gallery Grid */}
        {!loading && !error && items.length > 0 && (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredItems.map((item, index) => {
                const titleText = item.title?.da || item.title || 'Uden titel';
                const categoryText = item.category?.da || item.category || 'Diverse';
                const imageUrl = Array.isArray(item.image) ? item.image[0] : item.image;

                return (
                  <ScrollReveal
                    key={item.id}
                    variant="scale-up"
                    duration={600}
                    delay={(index % 3) * 100}
                  >
                    <div 
                      onClick={() => setLightboxIndex(index)}
                      className="group relative bg-white rounded-2xl overflow-hidden border border-stone-200 cursor-pointer shadow-sm hover:shadow-md hover:border-emerald-500/40 transition-all duration-300 hover:-translate-y-1"
                    >
                      {/* Image Container */}
                      <div className="aspect-[4/3] overflow-hidden relative bg-stone-100">
                        <img 
                          src={imageUrl} 
                          alt={titleText}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                        {/* Dark overlay on hover */}
                        <div className="absolute inset-0 bg-stone-950/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <div className="h-12 w-12 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-lg transform scale-75 group-hover:scale-100 transition-transform duration-300">
                            <ZoomIn className="h-5 w-5" />
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-5">
                        <span className="inline-block text-[10px] font-extrabold uppercase tracking-wider text-emerald-700 mb-3">
                          {categoryText}
                        </span>
                        <h3 className="text-base font-bold text-stone-900 group-hover:text-emerald-600 transition-colors line-clamp-1">
                          {titleText}
                        </h3>
                      </div>
                    </div>
                  </ScrollReveal>
                );
              })}
            </div>

            {filteredItems.length === 0 && (
              <div className="py-16 text-center text-stone-500">
                Ingen projekter fundet i denne kategori.
              </div>
            )}
          </div>
        )}

      </div>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && filteredItems[lightboxIndex] && (
        <div 
          className="fixed inset-0 z-50 bg-stone-950/95 backdrop-blur-sm flex flex-col justify-between p-4 md:p-6"
          onClick={() => setLightboxIndex(null)}
        >
          {/* Top Bar */}
          <div className="flex items-center justify-between w-full text-white py-2 z-10">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-400">
                {filteredItems[lightboxIndex].category?.da || filteredItems[lightboxIndex].category || 'Diverse'}
              </span>
              <h4 className="text-sm md:text-base font-bold text-stone-200">
                {filteredItems[lightboxIndex].title?.da || filteredItems[lightboxIndex].title || 'Uden titel'}
              </h4>
            </div>
            
            <button 
              onClick={() => setLightboxIndex(null)}
              className="p-2.5 rounded-full bg-stone-900 hover:bg-stone-800 text-stone-300 hover:text-white transition-colors cursor-pointer"
              aria-label="Luk"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Main Image View */}
          <div className="relative flex-1 flex items-center justify-center my-4">
            {/* Left Arrow */}
            <button 
              onClick={handlePrev}
              className="absolute left-2 md:left-4 p-3 rounded-full bg-stone-900/80 hover:bg-stone-800 text-white transition-colors z-10 cursor-pointer"
              aria-label="Forrige billede"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>

            {/* Image */}
            <div 
              className="max-w-full max-h-[70vh] md:max-h-[80vh] rounded-lg overflow-hidden shadow-2xl border border-stone-800"
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={Array.isArray(filteredItems[lightboxIndex].image) ? filteredItems[lightboxIndex].image[0] : filteredItems[lightboxIndex].image} 
                alt={filteredItems[lightboxIndex].title?.da || filteredItems[lightboxIndex].title}
                className="object-contain max-w-full max-h-[70vh] md:max-h-[80vh] mx-auto"
              />
            </div>

            {/* Right Arrow */}
            <button 
              onClick={handleNext}
              className="absolute right-2 md:right-4 p-3 rounded-full bg-stone-900/80 hover:bg-stone-800 text-white transition-colors z-10 cursor-pointer"
              aria-label="Næste billede"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>

          {/* Bottom Bar / Counter */}
          <div className="text-center text-stone-400 text-xs font-semibold py-2">
            Billede {lightboxIndex + 1} af {filteredItems.length}
          </div>
        </div>
      )}

      {/* CTA Section */}
      <div className="bg-stone-100 border-t border-stone-200 mt-12">
        <ScrollReveal variant="scale-up" duration={800}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
            <span className="inline-block text-xs font-extrabold uppercase tracking-widest text-emerald-800 mb-4">
              Kontakt Lasse i dag
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-stone-900 tracking-tight">
              Skal vi hjælpe dig med dit næste projekt?
            </h2>
            <p className="mt-4 text-stone-600 max-w-2xl mx-auto text-sm md:text-base">
              Uanset om det drejer sig om en ny fliseterrasse, klipning af hækken eller det helt store havedesign, står vi klar med professionel rådgivning og et uforpligtende tilbud.
            </p>
            
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <a
                href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`}
                className="flex items-center justify-center gap-2 w-full sm:w-auto bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-5 sm:px-8 py-2.5 sm:py-3.5 rounded-full shadow-lg transition-all hover:-translate-y-0.5 text-xs sm:text-base"
              >
                <Phone className="h-4 w-4 sm:h-5 sm:w-5" />
                Ring op: {contactInfo.phone}
              </a>
              <Link
                to="/"
                onClick={() => {
                  setTimeout(() => {
                    const el = document.getElementById('kontakt');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="flex items-center justify-center gap-2 w-full sm:w-auto bg-white hover:bg-stone-50 text-stone-800 font-bold px-5 sm:px-8 py-2.5 sm:py-3.5 rounded-full transition-all hover:-translate-y-0.5 border border-stone-300 shadow-sm text-xs sm:text-base"
              >
                <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" />
                Send os en besked
              </Link>
            </div>
          </div>
        </ScrollReveal>
      </div>

    </div>
  );
}
